import { Component, Input, OnInit } from '@angular/core';
import { LayoutService } from '@app/layout/layout.service';
import * as g from 'src/app/globals';

interface PriceHistory {
  DocDate: string;
  DocNum: number;
  CardCode: string;
  CardName: string;
  ItemCode: string;
  Dscription: string;
  PriceBefDi: number;
  DiscPrcnt: number;
  Price: number;
}

interface PriceHistoryFilter {
  singleValue: string;
  valueFrom: number | string | Date | null;
  valueTo: number | string | Date | null;
}

@Component({
  selector: 'app-price-history',
  templateUrl: './price-history.component.html',
  styleUrls: ['./price-history.component.less'],
})
export class PriceHistoryComponent implements OnInit {
  @Input() cardCode: string;
  @Input() itemCode: string;
  priceHistory: PriceHistory[] = [];
  filteredPriceHistory: PriceHistory[] = [];
  isLoading = true;
  error: string | null = null;
  selectedCardCode: string;
  readonly allCardCodeOption = 'all';
  filters: { [key: string]: PriceHistoryFilter } = {};

  itemDescription: string;
  cardName: string;

  global = g;

  allowShowAllInPriceHistory = false;

  constructor(private layoutService: LayoutService) {}

  ngOnInit(): void {
    this.allowShowAllInPriceHistory = this.global.allowShowAllInPriceHistory;
    this.priceHistory = [];
    this.selectedCardCode = this.cardCode;
    this.resetFilters();
    this.fetchSalesHistory();
  }

  get isAllCustomersSelected(): boolean {
    return this.selectedCardCode === this.allCardCodeOption;
  }

  onCardCodeSelectionChange(): void {
    this.resetFilters();
    this.fetchSalesHistory();
  }

  fetchSalesHistory(): void {
    this.isLoading = true;
    this.error = null;
    this.layoutService
      .executeQuery(80143, {
        CardCode: this.selectedCardCode,
        ItemCode: this.itemCode,
      })
      .subscribe((data: any[]) => {
        this.itemDescription = data.length > 0 ? data[0].Dscription : '';
        this.cardName = data.length > 0 ? data[0].CardName : '';

        this.priceHistory = data;
        this.filteredPriceHistory = [...this.priceHistory];
        this.isLoading = false;
      },
      (error) => {
        console.error('Failed to fetch price history', error);
        this.error = 'Failed to fetch price history';
        this.isLoading = false;
      });
  }

  resetFilters(): void {
    this.filters = {
      CardCode: { singleValue: '', valueFrom: null, valueTo: null },
      CardName: { singleValue: '', valueFrom: null, valueTo: null },
      DocDate: { singleValue: '', valueFrom: null, valueTo: null },
      DocNum: { singleValue: '', valueFrom: null, valueTo: null },
      ItemCode: { singleValue: '', valueFrom: null, valueTo: null },
      Dscription: { singleValue: '', valueFrom: null, valueTo: null },
      PriceBefDi: { singleValue: '', valueFrom: null, valueTo: null },
      DiscPrcnt: { singleValue: '', valueFrom: null, valueTo: null },
      Price: { singleValue: '', valueFrom: null, valueTo: null },
    };
  }

  applyFilters(): void {
    if (!this.isAllCustomersSelected) {
      this.filteredPriceHistory = [...this.priceHistory];
      return;
    }

    this.filteredPriceHistory = this.priceHistory.filter((record) => (
      this.matchesTextFilter(record.CardCode, this.filters.CardCode) &&
      this.matchesTextFilter(record.CardName, this.filters.CardName) &&
      this.matchesDateFilter(record.DocDate, this.filters.DocDate) &&
      this.matchesNumberFilter(record.DocNum, this.filters.DocNum) &&
      this.matchesTextFilter(record.ItemCode, this.filters.ItemCode) &&
      this.matchesTextFilter(record.Dscription, this.filters.Dscription) &&
      this.matchesNumberFilter(record.PriceBefDi, this.filters.PriceBefDi) &&
      this.matchesNumberFilter(record.DiscPrcnt, this.filters.DiscPrcnt) &&
      this.matchesNumberFilter(record.Price, this.filters.Price)
    ));
  }

  private matchesTextFilter(value: string, filter: PriceHistoryFilter): boolean {
    const search = (filter?.singleValue || '').trim().toLowerCase();

    if (!search) {
      return true;
    }

    return (value || '').toString().toLowerCase().includes(search);
  }

  private matchesNumberFilter(value: number, filter: PriceHistoryFilter): boolean {
    const from = this.toNullableNumber(filter?.valueFrom);
    const to = this.toNullableNumber(filter?.valueTo);

    if (from == null && to == null) {
      return true;
    }

    const numericValue = Number(value);

    if (isNaN(numericValue)) {
      return false;
    }

    return (
      (from == null || numericValue >= from) &&
      (to == null || numericValue <= to)
    );
  }

  private matchesDateFilter(value: string, filter: PriceHistoryFilter): boolean {
    const from = this.toDateOnly(filter?.valueFrom);
    const to = this.toDateOnly(filter?.valueTo);

    if (!from && !to) {
      return true;
    }

    const dateValue = this.toDateOnly(value);

    if (!dateValue) {
      return false;
    }

    return (!from || dateValue >= from) && (!to || dateValue <= to);
  }

  private toNullableNumber(value: number | string | Date | null): number | null {
    if (value == null || value === '') {
      return null;
    }

    const numericValue = Number(value);

    return isNaN(numericValue) ? null : numericValue;
  }

  private toDateOnly(value: string | number | Date | null): Date | null {
    if (!value) {
      return null;
    }

    const date = value instanceof Date
      ? new Date(value)
      : this.parseDateValue(value);

    if (isNaN(date.getTime())) {
      return null;
    }

    date.setHours(0, 0, 0, 0);
    return date;
  }

  private parseDateValue(value: string | number): Date {
    if (typeof value === 'string') {
      const dateParts = value.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);

      if (dateParts) {
        return new Date(
          Number(dateParts[3]),
          Number(dateParts[2]) - 1,
          Number(dateParts[1]),
        );
      }
    }

    return new Date(value);
  }
}
